import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, TooltipProps } from 'recharts';
import { Select } from 'antd';
import styles from './RevenueOverTimeChart.module.scss';

interface RevenueOverTimeData {
  date: string; // Changed from 'month' to a more generic 'date'
  revenue: number;
}

interface RevenueOverTimeChartProps {
  data: RevenueOverTimeData[];
  currency: string;
  selectedPeriod: number; // Receive the selected value as a prop
  onPeriodChange: (days: number) => void; // Receive the handler function as a prop
}

const CustomTooltip = ({ active, payload, label, currency }: TooltipProps<number, string> & { currency: string }) => {
  if (active && payload && payload.length) {
    const formatCurrency = (value: number) => {
      return new Intl.NumberFormat('sr-RS', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    };

    return (
      <div style={{
        backgroundColor: '#171f20',
        border: '1px solid #dab94f',
        borderRadius: '4px',
        padding: '10px',
        color: '#e9e9e9'
      }}>
        <p style={{ margin: '0 0 5px 0' }}>{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ margin: '0', color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value as number)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const timePeriods = [
  { label: 'Poslednjih 7 dana', value: 7 },
  { label: 'Poslednjih 30 dana', value: 30 },
  { label: 'Poslednja 3 meseca', value: 90 },
  { label: 'Poslednjih 6 meseci', value: 180 },
  { label: 'Poslednjih 12 meseci', value: 365 },
  { label: 'Sve vreme', value: 0 }
];

const RevenueOverTimeChart: React.FC<RevenueOverTimeChartProps> = ({ data, currency, selectedPeriod, onPeriodChange }) => {
  // We no longer need internal state or useMemo for filtering here.
  // The 'data' prop will always be the correct, up-to-date data from the parent.

  return (
    <div className={styles.chartContainer}>
      <div className={styles.header}>
        <h3>Prihod kroz vreme</h3>
        <Select
          value={selectedPeriod}       // The value is now controlled by the parent's state
          onChange={onPeriodChange}    // The onChange calls the function from the parent
          options={timePeriods}
          className={styles.select}
          style={{ minWidth: '200px' }}
        />
      </div>
      <div className={styles.chart}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}> {/* Use the data prop directly */}
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
            <XAxis dataKey="date" stroke="#e9e9e9" />
            <YAxis stroke="#e9e9e9" />
            <Tooltip content={<CustomTooltip currency={currency} />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="revenue"
              name="Prihod"
              stroke="#dab94f"
              strokeWidth={2}
              dot={{ fill: '#dab94f', strokeWidth: 2 }}
              activeDot={{ r: 6, fill: '#dab94f' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RevenueOverTimeChart;
